import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Key, Clock, LogOut, Check, AlertCircle, KeyRound, ExternalLink, Eye, EyeOff, RefreshCw, Power, PowerOff, Brain, X, Pencil, Trash2 } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

// Labels UX-friendly pour les champs du profil extrait par l'IA (doit matcher PROFILE_FIELDS backend)
const PROFILE_FIELD_LABELS = {
  nom: 'Nom', prenom: 'Prénom', entreprise: 'Entreprise', poste: 'Poste',
  secteur_activite: 'Secteur d\'activité', localisation: 'Localisation',
  langue_preferee: 'Langue préférée', centres_interet: 'Centres d\'intérêt',
  competences_declarees: 'Compétences déclarées', objectifs: 'Objectifs',
  ton_prefere: 'Ton préféré', contexte_utilisation: 'Contexte d\'utilisation',
};
const PROFILE_ARRAY_FIELDS = ['centres_interet', 'competences_declarees', 'objectifs'];

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [pwdMode, setPwdMode] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '' });
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [msg, setMsg] = useState(null);
  // S43 — Ma clé OpenRouter
  const [keyMode, setKeyMode] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [keySaving, setKeySaving] = useState(false);
  const [keyVerifying, setKeyVerifying] = useState(false);
  const [keyCredits, setKeyCredits] = useState(null);
  const [keyVerifyError, setKeyVerifyError] = useState(null);
  const [keyToggling, setKeyToggling] = useState(false);
  // P2 — Ma clé ElevenLabs (BYOK Studio Voice — obligatoire pour 2e cercle)
  const [elKeyMode, setElKeyMode] = useState(false);
  const [elKeyInput, setElKeyInput] = useState('');
  const [elKeyVisible, setElKeyVisible] = useState(false);
  const [elKeySaving, setElKeySaving] = useState(false);
  const [elKeyVerifying, setElKeyVerifying] = useState(false);
  const [elKeySubscription, setElKeySubscription] = useState(null);
  const [elKeyVerifyError, setElKeyVerifyError] = useState(null);
  const [elKeyToggling, setElKeyToggling] = useState(false);
  // Profil extrait par l'IA (ProfileExtractor) — purge et édition manuelle
  const [extracted, setExtracted] = useState(null);
  const [extractedLoading, setExtractedLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldDraft, setFieldDraft] = useState('');

  const token = localStorage.getItem('oracle_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadProfile = async () => {
    try {
      const [profRes, sessRes] = await Promise.all([
        fetch('/api/auth/me', { headers }),
        fetch('/api/auth/sessions', { headers })
      ]);
      if (profRes.ok) {
        const data = await profRes.json();
        setProfile(data);
        setForm({ full_name: data.full_name || '', email: data.email || '' });
      }
      if (sessRes.ok) setSessions(await sessRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadProfile(); loadExtracted(); }, []);

  const loadExtracted = async () => {
    setExtractedLoading(true);
    try {
      const r = await fetch('/api/auth/profile-extracted', { headers });
      if (r.ok) setExtracted(await r.json());
    } catch (e) { /* silent */ }
    setExtractedLoading(false);
  };

  const purgeExtracted = async () => {
    const confirmed = await confirmModal(
      'Purger totalement le profil extrait par l\'IA ? Tous les champs remémorés sur toi seront effacés.',
      { confirmText: 'Tout purger', danger: true }
    );
    if (!confirmed) return;
    try {
      const r = await fetch('/api/auth/profile-extracted', { method: 'DELETE', headers });
      if (r.ok) { toast.success('Profil IA purgé'); loadExtracted(); }
      else toast.error('Erreur purge');
    } catch { toast.error('Erreur purge'); }
  };

  const deleteField = async (field, value) => {
    try {
      const body = value !== undefined ? JSON.stringify({ value }) : undefined;
      const r = await fetch(`/api/auth/profile-extracted/field/${field}`, {
        method: 'DELETE', headers, body
      });
      if (r.ok) { toast.success('Supprimé'); loadExtracted(); }
      else toast.error('Erreur suppression');
    } catch { toast.error('Erreur suppression'); }
  };

  const saveField = async (field) => {
    const draft = fieldDraft.trim();
    if (!extracted?.profile_data) return;
    const current = { ...(extracted.profile_data || {}) };
    if (PROFILE_ARRAY_FIELDS.includes(field)) {
      current[field] = draft ? draft.split(',').map(s => s.trim()).filter(Boolean) : undefined;
      if (!current[field] || current[field].length === 0) delete current[field];
    } else {
      if (draft) current[field] = draft;
      else delete current[field];
    }
    try {
      const r = await fetch('/api/auth/profile-extracted', {
        method: 'PUT', headers,
        body: JSON.stringify({ profile_data: current })
      });
      if (r.ok) { toast.success('Champ mis à jour'); setEditingField(null); setFieldDraft(''); loadExtracted(); }
      else toast.error('Erreur mise à jour');
    } catch { toast.error('Erreur mise à jour'); }
  };

  // S43 — Auto-vérification de la clé si active, pour que le bandeau reflète la réalité
  useEffect(() => {
    if (profile?.api_key_active && !keyCredits && !keyVerifyError && !keyVerifying) {
      const timer = setTimeout(() => { verifyOpenRouterKey(); }, 300);
      return () => clearTimeout(timer);
    }
  }, [profile?.api_key_active]);

  // P2 — Auto-vérification ElevenLabs miroir
  useEffect(() => {
    if (profile?.elevenlabs_key_active && !elKeySubscription && !elKeyVerifyError && !elKeyVerifying) {
      const timer = setTimeout(() => { verifyElevenLabsKey(); }, 300);
      return () => clearTimeout(timer);
    }
  }, [profile?.elevenlabs_key_active]);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const saveProfile = async () => {
    try {
      const res = await fetch('/api/auth/me', { method: 'PUT', headers, body: JSON.stringify(form) });
      if (res.ok) { showMsg('Profil mis à jour'); setEditMode(false); loadProfile(); }
      else showMsg((await res.json()).error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  const changePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm) {
      return showMsg('Les mots de passe ne correspondent pas', 'error');
    }
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'PUT', headers,
        body: JSON.stringify({ current_password: pwdForm.current_password, new_password: pwdForm.new_password })
      });
      if (res.ok) { showMsg('Mot de passe modifié'); setPwdMode(false); setPwdForm({ current_password: '', new_password: '', confirm: '' }); }
      else showMsg((await res.json()).error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  // S43 — Ma clé OpenRouter
  const saveOpenRouterKey = async () => {
    const trimmed = (keyInput || '').trim();
    if (!/^sk-or-/.test(trimmed)) {
      return showMsg('La clé doit commencer par sk-or-', 'error');
    }
    setKeySaving(true);
    try {
      const res = await fetch('/api/auth/openrouter-key', {
        method: 'PUT', headers,
        body: JSON.stringify({ api_key: trimmed }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showMsg('Clé OpenRouter enregistrée');
        setKeyMode(false);
        setKeyInput('');
        setKeyVisible(false);
        setKeyVerifyError(null);
        setKeyCredits(null);
        await loadProfile();
        // Vérification automatique dans la foulée
        setTimeout(() => verifyOpenRouterKey(), 200);
      } else {
        showMsg(data.error || 'Erreur enregistrement', 'error');
      }
    } catch (e) { showMsg('Erreur réseau', 'error'); }
    setKeySaving(false);
  };

  const verifyOpenRouterKey = async () => {
    setKeyVerifying(true);
    setKeyVerifyError(null);
    try {
      const res = await fetch('/api/auth/verify-openrouter-key', { method: 'POST', headers });
      const data = await res.json();
      if (data.valid) {
        setKeyCredits(data.credits);
        setKeyVerifyError(null);
        showMsg('Clé vérifiée — valide');
        await loadProfile();
      } else {
        setKeyVerifyError(data.error || 'Clé invalide');
        setKeyCredits(null);
      }
    } catch (e) { setKeyVerifyError('Erreur réseau'); }
    setKeyVerifying(false);
  };

  const toggleOpenRouterKey = async () => {
    const isActive = profile?.api_key_active;
    if (isActive && !(await confirmModal('Désactiver votre clé OpenRouter ? Vos appels basculeront sur la clé plateforme. Réversible à tout moment.'))) return;
    setKeyToggling(true);
    try {
      const action = isActive ? 'disable' : 'enable';
      const res = await fetch(`/api/auth/openrouter-key/${action}`, { method: 'POST', headers });
      if (res.ok) {
        showMsg(isActive ? 'Clé désactivée — bascule plateforme' : 'Clé réactivée');
        setKeyCredits(null);
        setKeyVerifyError(null);
        await loadProfile();
      } else {
        showMsg('Erreur', 'error');
      }
    } catch (e) { showMsg('Erreur réseau', 'error'); }
    setKeyToggling(false);
  };

  // P2 — Ma clé ElevenLabs (BYOK Studio Voice)
  const saveElevenLabsKey = async () => {
    const trimmed = (elKeyInput || '').trim();
    if (!/^(sk_[A-Za-z0-9]{32,64}|[a-f0-9]{32})$/i.test(trimmed)) {
      return showMsg('Clé ElevenLabs invalide (format sk_... ou 32 chars hex)', 'error');
    }
    setElKeySaving(true);
    try {
      const res = await fetch('/api/auth/elevenlabs-key', {
        method: 'PUT', headers,
        body: JSON.stringify({ api_key: trimmed }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showMsg('Clé ElevenLabs enregistrée');
        setElKeyMode(false);
        setElKeyInput('');
        setElKeyVisible(false);
        setElKeyVerifyError(null);
        setElKeySubscription(null);
        await loadProfile();
        setTimeout(() => verifyElevenLabsKey(), 200);
      } else {
        showMsg(data.error || 'Erreur enregistrement', 'error');
      }
    } catch (e) { showMsg('Erreur réseau', 'error'); }
    setElKeySaving(false);
  };

  const verifyElevenLabsKey = async () => {
    setElKeyVerifying(true);
    setElKeyVerifyError(null);
    try {
      const res = await fetch('/api/auth/verify-elevenlabs-key', { method: 'POST', headers });
      const data = await res.json();
      if (data.valid) {
        setElKeySubscription(data.subscription);
        setElKeyVerifyError(null);
        showMsg('Clé ElevenLabs vérifiée — valide');
        await loadProfile();
      } else {
        setElKeyVerifyError(data.error || 'Clé invalide');
        setElKeySubscription(null);
      }
    } catch (e) { setElKeyVerifyError('Erreur réseau'); }
    setElKeyVerifying(false);
  };

  const toggleElevenLabsKey = async () => {
    const isActive = profile?.elevenlabs_key_active;
    if (isActive && !(await confirmModal('Désactiver votre clé ElevenLabs ? Studio Voice basculera sur la clé plateforme (ou sera bloqué pour les utilisateurs du 2e cercle). Réversible à tout moment.'))) return;
    setElKeyToggling(true);
    try {
      const action = isActive ? 'disable' : 'enable';
      const res = await fetch(`/api/auth/elevenlabs-key/${action}`, { method: 'POST', headers });
      if (res.ok) {
        showMsg(isActive ? 'Clé EL désactivée' : 'Clé EL réactivée');
        setElKeySubscription(null);
        setElKeyVerifyError(null);
        await loadProfile();
      } else {
        showMsg('Erreur', 'error');
      }
    } catch (e) { showMsg('Erreur réseau', 'error'); }
    setElKeyToggling(false);
  };

  const revokeSession = async (id) => {
    try {
      await fetch(`/api/auth/sessions/${id}`, { method: 'DELETE', headers });
      showMsg('Session révoquée');
      loadProfile();
    } catch (e) { showMsg('Erreur', 'error'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full"></div></div>;
  if (!profile) return <div className="text-center py-12 text-gray-500">Erreur de chargement du profil</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {msg && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${msg.type === 'error' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>
          {msg.type === 'error' ? <AlertCircle size={16} /> : <Check size={16} />} {msg.text}
        </div>
      )}

      {/* Profil */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><User size={22} /> Mon profil</h2>
          {!editMode && (
            <button onClick={() => setEditMode(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Modifier</button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">Nom complet</label>
              <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEditMode(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm">Annuler</button>
              <button onClick={saveProfile} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Enregistrer</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <User size={24} className="text-blue-600" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{profile.full_name || profile.username}</div>
                <div className="text-sm text-gray-400">@{profile.username}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="flex items-center gap-2 text-sm text-gray-600"><Shield size={15} className="text-gray-400" /> {profile.role}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={15} className="text-gray-400" /> {profile.email || 'Non renseigné'}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Clock size={15} className="text-gray-400" /> Dernière connexion : {formatDate(profile.last_login)}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Clock size={15} className="text-gray-400" /> Inscrit le {formatDate(profile.created_at)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Changer mot de passe */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Key size={20} /> Mot de passe</h2>
          {!pwdMode && (
            <button onClick={() => setPwdMode(true)} className="text-sm text-yellow-600 hover:text-yellow-700 font-medium">Changer</button>
          )}
        </div>

        {pwdMode ? (
          <div className="space-y-3">
            <input type="password" placeholder="Mot de passe actuel" value={pwdForm.current_password} onChange={e => setPwdForm({...pwdForm, current_password: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
            <input type="password" placeholder="Nouveau mot de passe" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
            <input type="password" placeholder="Confirmer le nouveau mot de passe" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
            <div className="flex gap-3 pt-2">
              <button onClick={() => setPwdMode(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm">Annuler</button>
              <button onClick={changePassword} className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium">Modifier le mot de passe</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Dernière modification : {formatDate(profile.updated_at)}</p>
        )}
      </div>

      {/* S43 — Ma clé OpenRouter */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <KeyRound size={20} /> Ma clé OpenRouter
          </h2>
          {!profile.has_openrouter_key && !keyMode && (
            <button onClick={() => setKeyMode(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ajouter ma clé
            </button>
          )}
        </div>

        {/* État 1 — Clé stockée (active ou désactivée) — bandeau reflète la réalité */}
        {profile.has_openrouter_key && !keyMode && (() => {
          const isActive = !!profile.api_key_active;
          const isInvalid = !!keyVerifyError;
          // Couleur bandeau : vert (active + valide), orange (active + invalide OU désactivée)
          const bannerTone = isActive && !isInvalid ? 'green' : 'orange';
          const bannerTitle = !isActive
            ? 'Clé désactivée'
            : isInvalid
              ? 'Clé invalide chez OpenRouter'
              : 'Clé active';
          const bannerText = !isActive
            ? 'Vos appels basculent sur la clé plateforme. Cliquez « Réactiver » pour reprendre votre facturation perso.'
            : isInvalid
              ? 'La plateforme prend le relais automatiquement. Remplacez votre clé pour reprendre votre facturation perso.'
              : 'Vos appels IA sont facturés sur votre compte OpenRouter.';
          const toneClasses = bannerTone === 'green'
            ? { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', title: 'text-green-900', body: 'text-green-700' }
            : { bg: 'bg-orange-50', border: 'border-orange-200', iconBg: 'bg-orange-500', title: 'text-orange-900', body: 'text-orange-700' };

          return (
            <div className="space-y-4">
              <div className={`flex items-center gap-3 p-3 ${toneClasses.bg} border ${toneClasses.border} rounded-lg`}>
                <div className={`w-8 h-8 rounded-full ${toneClasses.iconBg} flex items-center justify-center flex-shrink-0`}>
                  {bannerTone === 'green' ? <Check size={16} className="text-white" /> : <AlertCircle size={16} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${toneClasses.title}`}>{bannerTitle}</div>
                  <div className={`text-xs ${toneClasses.body}`}>{bannerText}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500 font-medium mb-1">Ajoutée le</div>
                  <div className="text-gray-900">{formatDate(profile.api_key_added_at)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500 font-medium mb-1">{!isActive ? 'Désactivée le' : 'Vérifiée le'}</div>
                  <div className="text-gray-900">
                    {!isActive
                      ? formatDate(profile.api_key_disabled_at)
                      : (profile.api_key_verified_at ? formatDate(profile.api_key_verified_at) : 'Jamais')}
                  </div>
                </div>
              </div>

              {isActive && keyCredits && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                  <div className="text-blue-900 font-medium mb-1">Crédits OpenRouter</div>
                  <div className="text-blue-700">
                    {typeof keyCredits.remaining === 'number' && `Restant : $${keyCredits.remaining.toFixed(2)}`}
                    {typeof keyCredits.usage === 'number' && typeof keyCredits.total === 'number' &&
                      ` · Utilisé : $${keyCredits.usage.toFixed(2)} / $${keyCredits.total.toFixed(2)}`}
                  </div>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                {isActive && (
                  <button
                    onClick={verifyOpenRouterKey}
                    disabled={keyVerifying}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                    <RefreshCw size={14} className={keyVerifying ? 'animate-spin' : ''} />
                    {keyVerifying ? 'Vérification...' : 'Vérifier'}
                  </button>
                )}
                <button
                  onClick={() => { setKeyMode(true); setKeyInput(''); }}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
                  Remplacer
                </button>
                <button
                  onClick={toggleOpenRouterKey}
                  disabled={keyToggling}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ml-auto ${
                    isActive
                      ? 'border border-orange-200 text-orange-600 hover:bg-orange-50'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  } disabled:opacity-50`}>
                  {isActive ? <><PowerOff size={14} /> Désactiver</> : <><Power size={14} /> Réactiver</>}
                </button>
              </div>
            </div>
          );
        })()}

        {/* État 2 — Pas de clé, mode édition off */}
        {!profile.has_openrouter_key && !keyMode && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 leading-relaxed">
              Utilisez votre propre clé OpenRouter pour que les appels IA soient facturés sur votre compte personnel.
              Sinon, la plateforme utilise sa clé par défaut.
            </p>
            <a
              href="https://openrouter.ai/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700">
              Créer une clé sur openrouter.ai <ExternalLink size={13} />
            </a>
          </div>
        )}

        {/* État 3 — Saisie clé */}
        {keyMode && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Collez votre clé OpenRouter (commence par <code className="text-blue-600 bg-blue-50 px-1 rounded">sk-or-</code>).
              Elle sera chiffrée avant stockage. Personne ne pourra la relire depuis la plateforme.
            </p>
            <div className="relative">
              <input
                type={keyVisible ? 'text' : 'password'}
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                placeholder="sk-or-v1-..."
                autoFocus
                className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setKeyVisible(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {keyVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setKeyMode(false); setKeyInput(''); setKeyVisible(false); }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm">
                Annuler
              </button>
              <button
                onClick={saveOpenRouterKey}
                disabled={keySaving || !keyInput.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {keySaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* P2 — Ma clé ElevenLabs (BYOK Studio Voice, obligatoire pour 2e cercle) */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <KeyRound size={20} /> Ma clé ElevenLabs
            {profile?.cercle === '2e_cercle' && (
              <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full">Requise pour le 2e cercle</span>
            )}
          </h2>
          {!profile?.has_elevenlabs_key && !elKeyMode && (
            <button onClick={() => setElKeyMode(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ajouter ma clé
            </button>
          )}
        </div>

        {/* État 1 — Clé stockée (active ou désactivée) */}
        {profile?.has_elevenlabs_key && !elKeyMode && (() => {
          const isActive = !!profile.elevenlabs_key_active;
          const isInvalid = !!elKeyVerifyError;
          const bannerTone = isActive && !isInvalid ? 'green' : 'orange';
          const bannerTitle = !isActive
            ? 'Clé désactivée'
            : isInvalid
              ? 'Clé invalide chez ElevenLabs'
              : 'Clé active';
          const bannerText = !isActive
            ? (profile?.cercle === '2e_cercle'
                ? 'Studio Voice est bloqué tant que la clé est désactivée. Réactivez-la pour reprendre TTS/STS.'
                : 'Studio Voice bascule sur la clé plateforme. Cliquez « Réactiver » pour reprendre votre facturation perso.')
            : isInvalid
              ? 'La clé est rejetée par ElevenLabs. Remplacez-la pour continuer à utiliser Studio Voice.'
              : 'Vos appels Studio Voice sont facturés sur votre compte ElevenLabs.';
          const toneClasses = bannerTone === 'green'
            ? { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', title: 'text-green-900', body: 'text-green-700' }
            : { bg: 'bg-orange-50', border: 'border-orange-200', iconBg: 'bg-orange-500', title: 'text-orange-900', body: 'text-orange-700' };

          return (
            <div className="space-y-4">
              <div className={`flex items-center gap-3 p-3 ${toneClasses.bg} border ${toneClasses.border} rounded-lg`}>
                <div className={`w-8 h-8 rounded-full ${toneClasses.iconBg} flex items-center justify-center flex-shrink-0`}>
                  {bannerTone === 'green' ? <Check size={16} className="text-white" /> : <AlertCircle size={16} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${toneClasses.title}`}>{bannerTitle}</div>
                  <div className={`text-xs ${toneClasses.body}`}>{bannerText}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500 font-medium mb-1">Ajoutée le</div>
                  <div className="text-gray-900">{formatDate(profile.elevenlabs_api_key_added_at)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500 font-medium mb-1">{!isActive ? 'Désactivée le' : 'Vérifiée le'}</div>
                  <div className="text-gray-900">
                    {!isActive
                      ? formatDate(profile.elevenlabs_api_key_disabled_at)
                      : (profile.elevenlabs_api_key_verified_at ? formatDate(profile.elevenlabs_api_key_verified_at) : 'Jamais')}
                  </div>
                </div>
              </div>

              {isActive && elKeySubscription && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                  <div className="text-blue-900 font-medium mb-1">Quota ElevenLabs ({elKeySubscription.tier || '—'})</div>
                  <div className="text-blue-700">
                    {typeof elKeySubscription.character_remaining === 'number' && `Restant : ${elKeySubscription.character_remaining.toLocaleString('fr-FR')} caractères`}
                    {typeof elKeySubscription.character_count === 'number' && typeof elKeySubscription.character_limit === 'number' &&
                      ` · Utilisé : ${elKeySubscription.character_count.toLocaleString('fr-FR')} / ${elKeySubscription.character_limit.toLocaleString('fr-FR')}`}
                  </div>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                {isActive && (
                  <button
                    onClick={verifyElevenLabsKey}
                    disabled={elKeyVerifying}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                    <RefreshCw size={14} className={elKeyVerifying ? 'animate-spin' : ''} />
                    {elKeyVerifying ? 'Vérification...' : 'Vérifier'}
                  </button>
                )}
                <button
                  onClick={() => { setElKeyMode(true); setElKeyInput(''); }}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
                  Remplacer
                </button>
                <button
                  onClick={toggleElevenLabsKey}
                  disabled={elKeyToggling}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ml-auto ${
                    isActive
                      ? 'border border-orange-200 text-orange-600 hover:bg-orange-50'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  } disabled:opacity-50`}>
                  {isActive ? <><PowerOff size={14} /> Désactiver</> : <><Power size={14} /> Réactiver</>}
                </button>
              </div>
            </div>
          );
        })()}

        {/* État 2 — Pas de clé, mode édition off */}
        {!profile?.has_elevenlabs_key && !elKeyMode && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 leading-relaxed">
              {profile?.cercle === '2e_cercle'
                ? 'Studio Voice (TTS / STS) est désactivé tant que vous n\'avez pas branché votre clé ElevenLabs personnelle. ITRIIA ne facture pas de crédits voix au 2e cercle — vous restez maître de vos coûts et de vos voix custom.'
                : 'Branchez votre clé ElevenLabs personnelle pour que Studio Voice utilise votre compte (voix clonées, quota perso). Sinon la plateforme utilise sa clé par défaut.'}
            </p>
            <a
              href="https://elevenlabs.io/app/settings/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700">
              Créer une clé sur elevenlabs.io <ExternalLink size={13} />
            </a>
          </div>
        )}

        {/* État 3 — Saisie clé */}
        {elKeyMode && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Collez votre clé ElevenLabs (commence par <code className="text-blue-600 bg-blue-50 px-1 rounded">sk_</code> pour les clés récentes, ou 32 chars hex pour les anciennes).
              Elle sera chiffrée AES-256-GCM avant stockage. Personne ne pourra la relire depuis la plateforme.
            </p>
            <div className="relative">
              <input
                type={elKeyVisible ? 'text' : 'password'}
                value={elKeyInput}
                onChange={e => setElKeyInput(e.target.value)}
                placeholder="sk_..."
                autoFocus
                className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setElKeyVisible(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {elKeyVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setElKeyMode(false); setElKeyInput(''); setElKeyVisible(false); }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm">
                Annuler
              </button>
              <button
                onClick={saveElevenLabsKey}
                disabled={elKeySaving || !elKeyInput.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {elKeySaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profil extrait par l'IA — contrôle total : voir, éditer, purger */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Brain size={20} /> Profil extrait par l'IA</h2>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              Informations extraites automatiquement à partir de tes messages pour adapter les réponses. Tu peux éditer ou purger ces données à tout moment — les prochaines conversations pourront les re-enrichir.
            </p>
          </div>
          {extracted?.completeness_score >= 0 && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-xs text-gray-500">Complétude</div>
              <div className="text-sm font-bold text-blue-600">{Math.round((extracted.completeness_score || 0) * 100)}%</div>
            </div>
          )}
        </div>

        {extractedLoading ? (
          <div className="text-sm text-gray-400 py-4">Chargement…</div>
        ) : !extracted || !extracted.profile_data || Object.keys(extracted.profile_data).length === 0 ? (
          <div className="text-sm text-gray-500 italic py-4 bg-gray-50 rounded-lg px-4">
            Aucune donnée extraite pour le moment. Ton profil se construira au fil de tes conversations.
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {Object.keys(PROFILE_FIELD_LABELS).map(field => {
                const value = extracted.profile_data[field];
                if (!value || (Array.isArray(value) && value.length === 0)) return null;
                const isArray = PROFILE_ARRAY_FIELDS.includes(field);
                const isEditing = editingField === field;
                return (
                  <div key={field} className="flex items-start gap-3 py-2 px-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="text-xs font-semibold text-gray-500 w-40 shrink-0 pt-1">{PROFILE_FIELD_LABELS[field]}</div>
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={fieldDraft}
                            onChange={e => setFieldDraft(e.target.value)}
                            autoFocus
                            placeholder={isArray ? 'élément 1, élément 2, ...' : 'valeur'}
                            className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveField(field);
                              if (e.key === 'Escape') { setEditingField(null); setFieldDraft(''); }
                            }}
                          />
                          <button onClick={() => saveField(field)} className="text-green-600 hover:text-green-700"><Check size={16} /></button>
                          <button onClick={() => { setEditingField(null); setFieldDraft(''); }} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                        </div>
                      ) : isArray && Array.isArray(value) ? (
                        <div className="flex flex-wrap gap-1.5">
                          {value.map((v, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-xs text-blue-700">
                              {v}
                              <button onClick={() => deleteField(field, v)} className="text-blue-400 hover:text-red-500 ml-0.5" title="Oublier cet élément">
                                <X size={11} />
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-800">{value}</div>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => {
                            setEditingField(field);
                            setFieldDraft(Array.isArray(value) ? value.join(', ') : String(value));
                          }}
                          title="Éditer"
                          className="text-gray-400 hover:text-blue-600 p-1">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => deleteField(field)} title="Oublier ce champ" className="text-gray-400 hover:text-red-500 p-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-400">
                {extracted.last_updated && `Dernière mise à jour : ${formatDate(extracted.last_updated)}`}
              </div>
              <button
                onClick={purgeExtracted}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors">
                <Trash2 size={13} /> Tout purger
              </button>
            </div>
          </>
        )}
      </div>

      {/* Sessions actives */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4"><Clock size={20} /> Sessions actives ({sessions.length})</h2>
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune session active</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((s, i) => {
              const meta = typeof s.metadata === 'string' ? JSON.parse(s.metadata) : (s.metadata || {});
              return (
                <div key={s.id || i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <div className="text-sm text-gray-700 font-medium">{meta.userAgent ? meta.userAgent.split(' ').slice(0, 3).join(' ') : 'Session'}</div>
                    <div className="text-xs text-gray-400">Connecté le {formatDate(s.created_at)} · Expire le {formatDate(s.expires_at)}</div>
                  </div>
                  <button onClick={() => revokeSession(s.id)} className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1">
                    <LogOut size={14} /> Révoquer
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
